import React, { useEffect, useState } from 'react';
import { getImportExclusions } from '../utils/djImportSelection';
import { meterState, meterLine, fmt } from '../utils/djLibraryMeter';
import { track } from '../utils/analytics';
import './DjLibraryMeter.scss';

const LABELS = { rekordbox: 'Rekordbox', serato: 'Serato', traktor: 'Traktor', virtualdj: 'Virtual DJ' };

// Session cache so re-opening a sidebar doesn't re-parse the DJ library every
// time. Keyed by user AND selection (account-switch safe per the user-scoped
// state rule; a changed selection simply misses the cache and recounts).
const _cache = new Map(); // `${app}:${userId}:${selection}` -> { coverage, at }
const CACHE_TTL_MS = 5 * 60 * 1000;
const selectionSig = (keys) => [...keys].sort().join('|');

/**
 * The DJ sidebars' standing library meter: how much of the app's collection
 * (unique importable files) is in the local library, drawn as three bar
 * segments — in MixGoat (fill), selected but still to import (gap), left out
 * of sync on purpose (hatched) — with one line under it saying why the rest
 * is missing and, for the excluded part, the exit ("Sync them" → Manage
 * imports). At rest with everything selected in there is no gap, so the bar
 * never looks like a stalled import; during a run the fill animates and the
 * line becomes live progress.
 *
 * Its own captioned section above the imported-playlist list — deliberately
 * NOT inside the "Last synced" card, which is about the action just taken.
 * `compact` (the not-yet-synced view) keeps the old bar + label only.
 *
 * Auto-refreshes when an import for this app finishes (the 'done' progress
 * phase fires on success, stop, and failure alike), when the library
 * location changes, and when the picker's exclusions change (cache key).
 * While the first parse runs a quiet skeleton reserves the space.
 */
export default function DjLibraryMeter({ app, exists, userId = null, sourcePath = null, importing = false, onManage = null, noun = 'playlist', compact = false }) {
    const label = LABELS[app] || app;
    const cacheKey = `${app}:${userId}:${selectionSig(getImportExclusions(app, userId))}`;
    const [coverage, setCoverage] = useState(() => _cache.get(cacheKey)?.coverage || null);
    const [failed, setFailed] = useState(false);
    // Last 'tracks' tick of a run for this app, and the tracks it created —
    // kept until the recount lands so the bar never falls back to the old
    // number between 'done' and the fresh parse.
    const [progress, setProgress] = useState(null);
    const [liveDone, setLiveDone] = useState(0);

    useEffect(() => {
        if (!exists) return undefined;
        let cancelled = false;
        const load = () => {
            const keys = getImportExclusions(app, userId);
            window.electron.djapp.importCoverage?.(app, sourcePath || undefined, keys).then((cov) => {
                if (cancelled) return;
                if (!cov?.total) { setFailed(true); return; }
                _cache.set(`${app}:${userId}:${selectionSig(keys)}`, { coverage: cov, at: Date.now() });
                setCoverage(cov);
                setLiveDone(0);
                setFailed(false);
            }).catch(() => { if (!cancelled) setFailed(true); });
        };
        const hit = _cache.get(cacheKey);
        if (hit) setCoverage(hit.coverage);
        if (!hit || Date.now() - hit.at > CACHE_TTL_MS) load();
        const off = window.electron.djapp.onImportProgress?.((p) => {
            if (p?.app !== app) return;
            if (p.phase === 'done') { setProgress(null); load(); }
            else if (p.phase === 'tracks') {
                setProgress(p);
                if (typeof p.newDone === 'number') setLiveDone(p.newDone);
            }
        });
        // Library location changed under us — old numbers describe the old
        // library; drop them and recount.
        const offLoc = window.electron.djlibrary?.onLocationChanged?.((d) => {
            if (d?.app !== app) return;
            _cache.delete(cacheKey);
            setCoverage(null);
            setFailed(false);
            load();
        });
        return () => { cancelled = true; off?.(); offLoc?.(); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [app, exists, userId, sourcePath, cacheKey]);

    if (!exists || (failed && !coverage)) return null;

    const cls = `dj-library-meter${compact ? ' compact' : ''}`;
    if (!coverage) {
        return (
            <div className={`${cls} loading`} aria-hidden="true">
                <div className="dj-library-meter-head">
                    {!compact && <h5>{label} library</h5>}
                    <span className="dj-library-meter-count-skeleton" />
                </div>
                <div className="dj-library-meter-bar" />
            </div>
        );
    }

    const st = meterState(coverage, { liveDone });
    const line = compact ? null : meterLine(coverage, { importing, progress, noun });
    const countText = compact
        ? `${fmt(st.imported)} of ${fmt(st.total)} tracks in MixGoat${importing ? ' · importing…' : ''}`
        : `${fmt(st.imported)} of ${fmt(st.total)} in MixGoat`;
    const onSyncThem = () => {
        track('dj_library_meter_sync_clicked', {
            app,
            not_synced: coverage.excluded || 0,
            playlists: coverage.excludedPlaylists || 0,
            loose: !!coverage.excludedLoose,
        });
        onManage?.();
    };

    return (
        <div className={cls} title={`Unique tracks from your ${label} collection already in your MixGoat library`}>
            <div className="dj-library-meter-head">
                {!compact && <h5>{label} library</h5>}
                <span className="dj-library-meter-count">{countText}</span>
            </div>
            <div className="dj-library-meter-bar">
                <div className={`dj-library-meter-fill${importing ? ' live' : ''}`} style={{ width: `${st.fillPct}%` }} />
                {st.excluded > 0 && (
                    <div className="dj-library-meter-excluded" style={{ width: `${st.excludedPct}%` }} title="Left out of sync on purpose" />
                )}
            </div>
            {line && (
                <span className="dj-library-meter-line">
                    {line.text}
                    {line.action === 'manage' && onManage && (
                        <>
                            {' · '}
                            <button type="button" className="dj-library-meter-action" onClick={onSyncThem} title="Choose which playlists and tracks stay synced">
                                Sync them
                            </button>
                        </>
                    )}
                </span>
            )}
        </div>
    );
}
